'use client';

import {googleLatLngLiteralToLatLng, latLngToGoogleLatLngLiteral, type SearchRadius} from '#/utils/geo';
import {CircleF, OverlayView, OverlayViewF} from '@react-google-maps/api';
import {computeDestinationPoint} from 'geolib';
import {useCallback, useMemo, useRef} from 'react';
import {useCurrentThemeColors} from '../store/themeColorsStore';

type Props = SearchRadius & {
  onCenterChanged?: (centerLatLng: google.maps.LatLngLiteral) => void;
  onRadiusChanged?: (radius: number) => void;
};

/**
 * A map search radius component.
 * Note: Here we are using the CircleF component from the @react-google-maps/api package because the Circle component does not rerender correctly in dev with reactStrictMode enabled.
 * https://github.com/JustFly1984/react-google-maps-api/issues/3095
 *
 * TODO: I have disabled the editable option for the circle for now because the full implementation needs ux design. When enabled the onRadiusChanged and onCenterChanged callbacks will need to be set in the parent component.
 */
export const MapSearchRadius = (props: Props) => {
  const {radius, title, center, onCenterChanged, onRadiusChanged} = props;
  const currentThemeColors = useCurrentThemeColors();
  const circleRef = useRef<google.maps.Circle | null>(null);

  const labelPosition = useMemo(
    () => latLngToGoogleLatLngLiteral(computeDestinationPoint(googleLatLngLiteralToLatLng(center), radius, 0)),
    [center, radius]
  );

  const handleLoad = useCallback((circle: google.maps.Circle) => {
    circleRef.current = circle;
  }, []);

  const handleCenterChanged = useCallback(() => {
    const center = circleRef.current?.getCenter();
    if (!center) return;
    const centerLatLng: google.maps.LatLngLiteral | undefined = {
      lat: center.lat(),
      lng: center.lng(),
    };
    if (!centerLatLng) return;
    onCenterChanged?.(centerLatLng);
  }, [onCenterChanged]);

  const handleRadiusChanged = useCallback(() => {
    const radius = circleRef.current?.getRadius();
    if (!radius) return;
    onRadiusChanged?.(radius);
  }, [onRadiusChanged]);

  return (
    <>
      <CircleF
        center={center}
        radius={radius}
        onLoad={handleLoad}
        onCenterChanged={handleCenterChanged}
        onRadiusChanged={handleRadiusChanged}
        options={{
          strokeWeight: 2,
          strokeColor: currentThemeColors.primary,
          fillColor: currentThemeColors.primary,
          fillOpacity: 0.2,
          zIndex: 1,
          // editable: true,
        }}
      />
      <OverlayViewF position={labelPosition} mapPaneName={OverlayView.OVERLAY_LAYER} zIndex={2}>
        <div className="-mt-10 flex items-center justify-center rounded-lg bg-primary p-3">
          <p className="font-bold text-white">{title}</p>
        </div>
      </OverlayViewF>
    </>
  );
};
